import { Link } from 'react-router-dom';

import LegalPageLayout, { SupportEmailLink } from './LegalPageLayout';

const EFFECTIVE_DATE = '28 September 2026';

export const TermsPage = () => (
  <LegalPageLayout
    title="ASiJoLab Terms of Use"
    pageTitle="Terms of Use | ASiJoLab"
    description="Terms governing the use of ASiJoLab engineering information, tools and services."
    effectiveDate={EFFECTIVE_DATE}
  >
    <p>These Terms of Use govern your use of ASiJoLab and its websites, databases, calculators, tools and related services.</p>
    <p>By using ASiJoLab, you agree to these Terms.</p>

    <section>
      <h2>1. About ASiJoLab</h2>
      <p>ASiJoLab is an independent project.</p>
      <p>ASiJoLab provides engineering reference information and practical tools primarily intended for machining, manufacturing and related technical work.</p>
      <p>The project may include, among other things:</p>
      <ul>
        <li>material data,</li>
        <li>machining-related material comparisons,</li>
        <li>hardness conversions,</li>
        <li>dimensional tolerances and fits,</li>
        <li>gauge calculations,</li>
        <li>reamer calculations,</li>
        <li>thread data,</li>
        <li>machining calculators,</li>
        <li>and other engineering reference tools.</li>
      </ul>
      <p>ASiJoLab is continuously developed and its available features may change over time.</p>
    </section>

    <section>
      <h2>2. Engineering information and calculations</h2>
      <p>ASiJoLab is intended as an engineering reference and productivity tool.</p>
      <p>Calculations, recommendations, conversions, material information, machining parameters and other technical results provided by ASiJoLab should be treated as reference information or starting values.</p>
      <p>Actual machining and manufacturing results can depend on many factors, including:</p>
      <ul>
        <li>machine condition and rigidity,</li><li>tooling,</li><li>tool holding,</li><li>workholding,</li><li>material condition,</li><li>heat treatment,</li><li>coolant,</li><li>machining strategy,</li><li>measurement equipment,</li><li>environmental conditions,</li><li>and other production variables.</li>
      </ul>
      <p>You are responsible for verifying critical dimensions, tolerances, cutting parameters and other technical information before using them in production.</p>
      <p>Where compliance with a particular standard, drawing, customer specification or safety requirement is important, the applicable official standard or documentation should take precedence.</p>
    </section>

    <section>
      <h2>3. Accuracy of information</h2>
      <p>ASiJoLab aims to provide useful and accurate information, but errors, omissions or outdated information may occur.</p>
      <p>ASiJoLab does not guarantee that:</p>
      <ul>
        <li>every value is error-free,</li><li>every database entry is complete,</li><li>every calculation is suitable for every application,</li><li>every referenced standard remains unchanged,</li><li>or every tool will always produce a result appropriate for a specific manufacturing process.</li>
      </ul>
      <p>If you identify a possible error, you are encouraged to report it so it can be reviewed.</p>
    </section>

    <section>
      <h2>4. Use of ASiJoLab</h2>
      <p>You may use ASiJoLab for normal personal, educational and professional engineering purposes.</p>
      <p>You must not intentionally:</p>
      <ul>
        <li>interfere with the operation or security of ASiJoLab,</li><li>attempt to gain unauthorized access to accounts, systems or administrative functions,</li><li>distribute malware or malicious requests through the service,</li><li>use automated systems to excessively overload the service,</li><li>systematically copy or extract substantial parts of ASiJoLab databases for redistribution,</li><li>reproduce ASiJoLab content as a competing database or service without permission.</li>
      </ul>
      <p>Normal use of search engines and ordinary indexing is not prohibited by this section.</p>
    </section>

    <section>
      <h2>5. Accounts</h2>
      <p>Some ASiJoLab functions may require a registered account.</p>
      <p>You are responsible for:</p>
      <ul><li>providing a valid email address,</li><li>keeping your login credentials secure,</li><li>and notifying ASiJoLab if you believe your account has been compromised.</li></ul>
      <p>ASiJoLab may restrict or disable an account where reasonably necessary to protect the service, its users or its security.</p>
    </section>

    <section>
      <h2>6. User-submitted information</h2>
      <p>ASiJoLab may allow users to submit information such as Material Requests.</p>
      <p>Do not submit confidential, proprietary, export-controlled or customer-restricted information unless you have the right to provide it.</p>
      <p>Information submitted through features such as Material Requests may be used to:</p>
      <ul><li>operate the requested feature,</li><li>improve ASiJoLab,</li><li>expand its databases,</li><li>or prioritize future development.</li></ul>
      <p>Submitting a request does not guarantee that the requested material, feature or information will be added.</p>
    </section>

    <section>
      <h2>7. Voluntary support</h2>
      <p>ASiJoLab is currently free to use.</p>
      <p>Users may voluntarily support the continued development and operation of ASiJoLab through monthly or one-time payments.</p>
      <p>Financial support may help cover costs such as:</p>
      <ul><li>development,</li><li>hosting,</li><li>infrastructure,</li><li>email delivery,</li><li>database services,</li><li>and other operating expenses.</li></ul>
      <p>Supporting ASiJoLab does not currently unlock any additional features, tools or content.</p>
      <p>All currently available ASiJoLab functionality remains available regardless of whether a user financially supports the project.</p>
      <p>Supporter and Supporter Plus labels are forms of recognition for users who support the project and do not represent paid access to additional functionality.</p>
      <h3>Monthly support</h3>
      <p>Monthly support continues until cancelled.</p>
      <p>Users may cancel recurring support at any time through the available subscription-management process.</p>
      <p>Cancellation prevents future recurring charges according to the payment provider's billing process.</p>
      <h3>One-time support</h3>
      <p>One-time support is a single voluntary payment and does not create an ongoing subscription or entitlement to additional functionality.</p>
      <p>Payments are processed by Stripe. ASiJoLab does not receive or store complete payment-card numbers or CVC codes.</p>
      <p>If you believe a payment was made incorrectly or there is a problem with a payment, please <Link to="/contact">contact ASiJoLab</Link>.</p>
    </section>

    <section>
      <h2>8. Intellectual property and database content</h2>
      <p>ASiJoLab contains original software, database structures, calculations, written explanations, graphics and other content.</p>
      <p>Except where otherwise stated, this content may not be systematically copied, republished, sold or used to create a substantially reproduced competing database or service without permission.</p>
      <p>Individual technical facts, material designations, standard designations and similar factual information may originate from publicly available or third-party technical sources and are not claimed as exclusive ASiJoLab property merely because they appear on the website.</p>
      <p>Third-party trademarks, standards and product names remain the property of their respective owners.</p>
    </section>

    <section>
      <h2>9. External sources and third-party services</h2>
      <p>ASiJoLab may rely on or refer to information from:</p>
      <ul><li>technical standards,</li><li>manufacturer documentation,</li><li>catalogues,</li><li>published engineering references,</li><li>and other external sources.</li></ul>
      <p>ASiJoLab may also use third-party infrastructure or services such as hosting, database, email-delivery and payment providers.</p>
      <p>ASiJoLab is not responsible for the independent operation, availability or policies of third-party services.</p>
    </section>

    <section>
      <h2>10. Availability and changes</h2>
      <p>ASiJoLab may:</p>
      <ul><li>add or remove features,</li><li>modify calculations or datasets,</li><li>correct errors,</li><li>change the user interface,</li><li>temporarily suspend parts of the service,</li><li>or discontinue parts of the project.</li></ul>
      <p>There is no guarantee that any particular feature will remain available indefinitely.</p>
      <p>Where practical, significant technical changes may be communicated to users, but ASiJoLab is not required to preserve previous versions of every tool or dataset.</p>
    </section>

    <section>
      <h2>11. Responsibility for use</h2>
      <p>You remain responsible for decisions made using information from ASiJoLab.</p>
      <p>ASiJoLab should not be used as the sole basis for decisions where an incorrect value could create a significant safety risk, regulatory violation, damage to machinery, defective critical components or other serious consequences.</p>
      <p>Appropriate professional judgment, measurement, verification and applicable official documentation should be used where required.</p>
    </section>

    <section>
      <h2>12. Limitation of liability</h2>
      <p>To the maximum extent permitted by applicable law, ASiJoLab is provided on an “as available” basis without a guarantee that it will always be error-free, uninterrupted or suitable for a particular manufacturing application.</p>
      <p>ASiJoLab is not intended to replace professional engineering judgment, official standards, machine or tool manufacturer instructions, customer specifications or required quality-control procedures.</p>
      <p>Nothing in these Terms excludes or limits liability where such exclusion or limitation is prohibited by applicable law.</p>
    </section>

    <section>
      <h2>13. Privacy and local storage</h2>
      <p>Use of personal data is described separately in the <Link to="/privacy">Privacy Policy</Link>.</p>
      <p>Information about cookies, local storage and similar browser technologies is described in <Link to="/cookies">Cookies &amp; Local Storage</Link>.</p>
      <p>ASiJoLab currently does not use advertising or behavioural marketing trackers.</p>
    </section>

    <section>
      <h2>14. Changes to these Terms</h2>
      <p>These Terms may be updated as ASiJoLab develops.</p>
      <p>The current version and effective date will be published on this page.</p>
      <p>Material changes may be communicated through the website where appropriate.</p>
      <p>Continued use of ASiJoLab after updated Terms become effective constitutes acceptance of the updated Terms to the extent permitted by applicable law.</p>
    </section>

    <section>
      <h2>15. Applicable law</h2>
      <p>These Terms are governed by the laws of the Czech Republic, subject to any mandatory rights or protections that cannot legally be excluded under applicable law.</p>
    </section>

    <section>
      <h2>16. Contact</h2>
      <p>Questions regarding these Terms or ASiJoLab may be sent to:</p>
      <div className="legalIdentityBlock"><span>ASiJoLab</span><SupportEmailLink /></div>
    </section>
  </LegalPageLayout>
);

export const PrivacyPage = () => (
  <LegalPageLayout
    title="ASiJoLab Privacy Policy"
    pageTitle="Privacy Policy | ASiJoLab"
    description="Information about how ASiJoLab processes and protects personal data."
    effectiveDate={EFFECTIVE_DATE}
  >
    <p>This Privacy Policy explains what personal data ASiJoLab processes, why it is processed, and how it is handled.</p>

    <section>
      <h2>1. Data controller</h2>
      <div className="legalIdentityBlock"><span>ASiJoLab — an independent project</span><span>Contact: <SupportEmailLink /></span></div>
    </section>

    <section>
      <h2>2. Data we collect</h2>
      <h3>Account data</h3>
      <p>When you create an ASiJoLab account, we may store:</p>
      <ul><li>your email address,</li><li>a securely hashed version of your password,</li><li>account role and support level,</li><li>account status,</li><li>email verification status,</li><li>account creation and update timestamps,</li><li>last login timestamp,</li><li>email-verification, password-reset and email-change token hashes and expiration times,</li><li>email-delivery timestamps and counters,</li><li>a pending new email address when you request an email change.</li></ul>
      <p>Passwords are not stored in plain text. They are stored as secure password hashes.</p>
      <p>Verification, password-reset and email-change tokens are stored as hashes in the database.</p>
      <p>Providing an email address and password is necessary to create an ASiJoLab account. If you do not provide them, an account cannot be created.</p>
      <p>Financial support is optional.</p>
      <h3>Material Requests</h3>
      <p>If you submit a Material Request, ASiJoLab stores:</p>
      <ul><li>the requested material name,</li><li>your user ID,</li><li>your email address,</li><li>the date and time of the request.</li></ul>
      <p>Requester email addresses and user IDs are not exposed to other ordinary users through the shared Material Requests interface.</p>
      <h3>Voluntary support and Stripe data</h3>
      <p>If you financially support ASiJoLab through Stripe, ASiJoLab may store:</p>
      <ul><li>Stripe customer ID,</li><li>Stripe subscription ID,</li><li>selected support option,</li><li>subscription status,</li><li>subscription billing-period information,</li><li>cancellation status,</li><li>timestamps of successful or failed support payments,</li><li>your ASiJoLab support level.</li></ul>
      <p>ASiJoLab does not receive or store:</p>
      <ul><li>your full card number,</li><li>CVC,</li><li>complete payment-card details.</li></ul>
      <p>Payment-card information is entered directly on Stripe's hosted payment pages.</p>
    </section>

    <section>
      <h2>3. Why we use this data</h2>
      <p>Personal data is used only where needed to operate ASiJoLab, including:</p>
      <ul><li>creating and managing user accounts,</li><li>authenticating users,</li><li>verifying email addresses,</li><li>resetting passwords,</li><li>changing account email addresses,</li><li>maintaining account security,</li><li>processing Material Requests,</li><li>processing and managing voluntary support through Stripe,</li><li>sending necessary transactional account emails,</li><li>preventing abuse and maintaining the security and reliability of the service.</li></ul>
      <p>ASiJoLab does not currently use account data for advertising or behavioural marketing.</p>
    </section>

    <section>
      <h2>4. Legal bases for processing</h2>
      <p>ASiJoLab processes personal data only where there is an applicable legal basis.</p>
      <p>Depending on the purpose, processing may be based on:</p>
      <ul><li>performance of a contract or steps requested by you — for creating and operating your account, authentication, account management and processing actions you request through ASiJoLab;</li><li>legitimate interests — for maintaining the security, reliability and technical operation of ASiJoLab, preventing abuse and troubleshooting errors;</li><li>legal obligations — where information must be retained or processed to comply with applicable legal requirements, including obligations related to payments where applicable.</li></ul>
      <p>Voluntary financial support and related payment information are processed as necessary to handle the payment or subscription you request and to maintain the associated payment records.</p>
      <p>ASiJoLab does not rely on consent for advertising or behavioural marketing because ASiJoLab does not currently perform such processing.</p>
    </section>

    <section>
      <h2>5. Email communication</h2>
      <p>ASiJoLab uses Resend to send transactional emails such as:</p>
      <ul><li>email verification,</li><li>password-reset messages,</li><li>verification of a new email address,</li><li>notification that an email address has changed.</li></ul>
      <p>These messages may contain temporary verification or reset links.</p>
      <p>ASiJoLab also uses Resend to receive email sent to <SupportEmailLink />.</p>
      <p>ASiJoLab does not currently operate a marketing newsletter or promotional mailing list.</p>
    </section>

    <section>
      <h2>6. Cookies and browser storage</h2>
      <p>ASiJoLab currently uses:</p>
      <ul><li>one necessary authentication cookie named <code>asijolab_auth</code>,</li><li>local browser storage for the selected Metric/Inch unit preference.</li></ul>
      <p>ASiJoLab does not currently use Google Analytics, Meta Pixel, advertising trackers, fingerprinting or similar behavioural tracking technologies.</p>
      <p>More information is available on the <Link to="/cookies">Cookies &amp; Local Storage</Link> page.</p>
    </section>

    <section>
      <h2>7. Technical and server data</h2>
      <p>ASiJoLab itself does not deliberately store visitor IP addresses in its application database.</p>
      <p>The application may create technical logs containing information such as:</p>
      <ul><li>requested material search terms,</li><li>material IDs,</li><li>error information,</li><li>technical payment-related errors,</li><li>other operational information required for troubleshooting.</li></ul>
      <p>The hosting provider may independently process normal request information such as IP address, request path, status and timing as part of its infrastructure logs.</p>
    </section>

    <section>
      <h2>8. Third-party services</h2>
      <p>ASiJoLab currently relies on several external services to operate:</p>
      <h3>Heroku</h3><p>Application hosting, server runtime and infrastructure.</p>
      <h3>MongoDB Atlas</h3><p>Database hosting.</p>
      <h3>Resend</h3><p>Transactional email sending and receiving.</p>
      <h3>Stripe</h3><p>Hosted payment processing, recurring support and one-time voluntary support.</p>
      <p>These providers process data necessary to provide their respective services. Their own privacy and data-processing terms may also apply.</p>
    </section>

    <section>
      <h2>9. Data retention</h2>
      <p>ASiJoLab keeps personal data only for as long as it is reasonably needed to operate the service, maintain security, manage accounts and support payments, or meet applicable legal obligations.</p>
      <p>Some specific technical expiration periods currently include:</p>
      <ul><li>authentication cookie: 30 days,</li><li>email-verification token: 24 hours,</li><li>password-reset token: 1 hour,</li><li>email-change token: 24 hours.</li></ul>
      <p>Successful use of a verification/reset token removes the active token hash and expiration information.</p>
      <p>ASiJoLab does not currently have automatic deletion periods for all account, Material Request or Stripe-related records.</p>
      <p>If you want your personal data reviewed, corrected or deleted, contact:</p>
      <SupportEmailLink />
    </section>

    <section>
      <h2>10. Account deletion and data requests</h2>
      <p>ASiJoLab does not currently provide an automatic self-service account deletion button.</p>
      <p>You may contact <SupportEmailLink /> to exercise applicable data-protection rights, including:</p>
      <ul><li>access to your personal data,</li><li>correction of inaccurate personal data,</li><li>deletion of personal data where applicable,</li><li>restriction of processing,</li><li>data portability where applicable,</li><li>objection to processing based on legitimate interests.</li></ul>
      <p>You also have the right to lodge a complaint with the competent data-protection supervisory authority. In the Czech Republic, this is the Office for Personal Data Protection (ÚOOÚ).</p>
      <p>Requests will be reviewed and handled according to applicable data-protection requirements.</p>
    </section>

    <section>
      <h2>11. Security</h2>
      <p>ASiJoLab uses technical measures intended to protect account information, including:</p>
      <ul><li>hashed passwords,</li><li>hashed verification and reset tokens,</li><li>HttpOnly authentication cookies,</li><li>Secure cookies in production,</li><li>signed authentication tokens,</li><li>hosted Stripe Checkout so payment-card information does not pass through ASiJoLab servers.</li></ul>
      <p>However, no internet service can guarantee absolute security.</p>
    </section>

    <section>
      <h2>12. Children</h2>
      <p>ASiJoLab is designed as an engineering and manufacturing reference service and is not specifically directed at children.</p>
      <p>ASiJoLab does not knowingly seek to collect personal information from children for advertising or profiling purposes.</p>
    </section>

    <section>
      <h2>13. Changes to this Privacy Policy</h2>
      <p>This Privacy Policy may be updated as ASiJoLab develops or its data-processing practices change.</p>
      <p>The latest version and effective date will always be published on this page.</p>
    </section>

    <section>
      <h2>14. Contact</h2>
      <p>For privacy questions, data requests, account-related privacy matters or other questions about this Policy:</p>
      <div className="legalIdentityBlock"><span>ASiJoLab — an independent project</span><SupportEmailLink /></div>
    </section>
  </LegalPageLayout>
);

export const CookiesPage = () => (
  <LegalPageLayout
    title="ASiJoLab Cookies & Local Storage"
    pageTitle="Cookies & Local Storage | ASiJoLab"
    description="Information about the necessary cookie and local-storage preference used by ASiJoLab."
    effectiveDate={EFFECTIVE_DATE}
  >
    <p>ASiJoLab uses only a small amount of browser storage required for authentication and basic user preferences.</p>
    <p>ASiJoLab does not currently use advertising cookies, behavioural tracking, analytics platforms or marketing pixels.</p>

    <section>
      <h2>1. Authentication cookie</h2>
      <p>ASiJoLab uses one first-party authentication cookie:</p>
      <div className="legalTableWrap">
        <table className="legalTable"><tbody>
          <tr><th scope="row">Name</th><td><code>asijolab_auth</code></td></tr>
          <tr><th scope="row">Purpose</th><td>Keeps a registered user signed in</td></tr>
          <tr><th scope="row">Type</th><td>Necessary authentication cookie</td></tr>
          <tr><th scope="row">Lifetime</th><td>Up to 30 days</td></tr>
        </tbody></table>
      </div>
      <p>The <code>asijolab_auth</code> cookie contains a signed authentication token.</p>
      <p>It is:</p>
      <ul><li>HttpOnly, so it cannot be read by normal browser JavaScript,</li><li>Secure in production,</li><li>configured with SameSite=Lax,</li><li>used only for authentication and account access.</li></ul>
      <p>It is removed when the user logs out and otherwise expires after approximately 30 days.</p>
    </section>

    <section>
      <h2>2. Local Storage</h2>
      <p>ASiJoLab currently uses one local browser-storage preference:</p>
      <div className="legalTableWrap">
        <table className="legalTable"><tbody>
          <tr><th scope="row">Key</th><td><code>asijolab.unitSystem</code></td></tr>
          <tr><th scope="row">Purpose</th><td>Remembers your preferred measurement system</td></tr>
          <tr><th scope="row">Values</th><td>metric or inch</td></tr>
        </tbody></table>
      </div>
      <p>This preference contains no account, payment or authentication information.</p>
      <p>It remains in your browser so ASiJoLab can remember your unit preference between visits.</p>
    </section>

    <section>
      <h2>3. Other browser storage</h2>
      <p>ASiJoLab currently does not use:</p>
      <ul><li>sessionStorage,</li><li>IndexedDB,</li><li>advertising identifiers,</li><li>browser fingerprinting,</li><li>Google Analytics,</li><li>Google Tag Manager,</li><li>Meta Pixel,</li><li>Hotjar,</li><li>Mixpanel,</li><li>or similar tracking technologies.</li></ul>
    </section>

    <section>
      <h2>4. Third-party services</h2>
      <p>Some external services used by ASiJoLab may use their own cookies or browser technologies when you interact directly with them.</p>
      <p>For example, voluntary support payments are completed on Stripe-hosted Checkout pages. Cookies used on Stripe's own websites are controlled by Stripe and are not created or read by ASiJoLab.</p>
    </section>

    <section>
      <h2>5. Cookie consent</h2>
      <p>ASiJoLab currently uses its own browser storage only for functionality such as authentication and remembering user preferences.</p>
      <p>For this reason, ASiJoLab does not currently display a cookie-consent banner.</p>
      <p>If ASiJoLab later introduces analytics, advertising or other non-essential tracking technologies, this page and the consent mechanism will be updated where required.</p>
    </section>

    <section>
      <h2>6. Removing stored data</h2>
      <p>You can remove ASiJoLab cookies and local-storage preferences through your browser settings.</p>
      <p>Removing the authentication cookie will sign you out.</p>
      <p>Removing the unit preference will cause ASiJoLab to use its default measurement system again until you select another preference.</p>
    </section>

    <section>
      <h2>7. Changes</h2>
      <p>This page may be updated if ASiJoLab changes how cookies, local storage or similar technologies are used.</p>
      <p>The current version and effective date will always be published here.</p>
    </section>

    <section>
      <h2>8. Contact</h2>
      <p>Questions about cookies or browser storage can be sent to:</p>
      <div className="legalIdentityBlock"><span>ASiJoLab — an independent project</span><SupportEmailLink /></div>
    </section>
  </LegalPageLayout>
);

export const ContactPage = () => (
  <LegalPageLayout
    title="Contact"
    pageTitle="Contact | ASiJoLab"
    description="Contact ASiJoLab about technical issues, accounts, privacy requests or voluntary support."
  >
    <p>For questions about ASiJoLab, technical issues, account matters, privacy requests, or other enquiries, contact:</p>
    <div className="legalIdentityBlock">
      <span>ASiJoLab — an independent project</span>
      <SupportEmailLink />
    </div>
    <p>You can also use this address to report:</p>
    <ul><li>incorrect technical data,</li><li>calculation issues,</li><li>account or login problems,</li><li>payment or support issues,</li><li>privacy or personal-data requests.</li></ul>
  </LegalPageLayout>
);
